import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/client';
import { Smartphone, CheckCircle, AlertCircle, ArrowLeft, Shield } from 'lucide-react';
import './RegistrationPaymentPage.css';

const RegistrationPaymentPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [paid, setPaid] = useState(false);
  const [isDefaultPhone, setIsDefaultPhone] = useState(false);
  const [feeAmount, setFeeAmount] = useState(null);
  const [feeLoading, setFeeLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const phoneFromState = location.state?.phoneNumber;
    const phoneFromUser = user?.phoneNumber;
    const phoneFromStorage = localStorage.getItem('wa_phone');
    
    let defaultPhone = phoneFromState || phoneFromUser || phoneFromStorage || '';
    defaultPhone = defaultPhone.replace(/[\s+\-]/g, '');
    
    setPhoneNumber(defaultPhone);
    
    if (phoneFromState || phoneFromUser) {
      setIsDefaultPhone(true);
    }
  }, [location.state, user]);

  useEffect(() => {
    if (user?.hasPaidRegistration) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchFee = async () => {
      try {
        const response = await api.get('/api/payments/registration-fee');
        if (response.data && response.data.amount) {
          setFeeAmount(response.data.amount);
        }
      } catch (err) {
        console.error('Failed to fetch registration fee:', err);
      } finally {
        setFeeLoading(false);
      }
    };
    fetchFee();
  }, []);

  const handlePayment = async () => {
    const cleanedPhone = phoneNumber.replace(/[\s+\-]/g, '');
    
    if (!cleanedPhone) {
      setError('Please enter your phone number');
      return;
    }
    
    if (!/^255\d{9}$/.test(cleanedPhone) && !/^0\d{9}$/.test(cleanedPhone)) {
      setError('Please enter a valid Tanzania phone number (e.g., 255712345678 or 0712345678)');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api.post('/api/payments/register', {
        msisdn: cleanedPhone
      });

      if (response.data.success) {
        setMessage('✅ ' + response.data.message);

        const checkInterval = setInterval(async () => {
          try {
            const statusRes = await api.get('/api/payments/registration-status');
            if (statusRes.data.paid) {
              clearInterval(checkInterval);
              setPaid(true);
              setMessage('✅ Payment confirmed! Redirecting to login...');
              
              localStorage.removeItem('wa_token');
              localStorage.removeItem('wa_has_paid');
              localStorage.removeItem('wa_phone');
              
              setTimeout(() => navigate('/login'), 2000);
            }
          } catch (err) {
            console.error('Error checking payment status:', err);
          }
        }, 3000);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!paid) {
            setError('Payment taking longer than expected. Please check your phone and try logging in again.');
          }
        }, 60000);

      } else {
        setError('❌ ' + (response.data.message || 'Payment failed'));
      }
    } catch (err) {
      setError('❌ ' + (err.response?.data?.message || 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[\s+\-]/g, '');
    setPhoneNumber(value);
    setIsDefaultPhone(false);
    if (error) setError('');
  };

  const formattedFee = feeAmount ? Number(feeAmount).toLocaleString() : '5,000';

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-card animate-slide-up">
          <div className="payment-header">
            <div className="payment-icon-wrapper">
              <Smartphone size={32} />
            </div>
            <h1 className="payment-title">Complete Registration</h1>
            <p className="payment-subtitle">Pay registration fee to activate your account</p>
          </div>

          <div className="fee-display">
            <span className="fee-label">Registration Fee</span>
            <span className="fee-amount">
              {feeLoading ? (
                <span style={{ fontSize: '14px', color: '#94A3B8' }}>Loading...</span>
              ) : (
                <>
                  TZS {formattedFee} <span>(one-time)</span>
                </>
              )}
            </span>
          </div>

          <form className="payment-form" onSubmit={(e) => { e.preventDefault(); handlePayment(); }}>
            <div className="form-group">
              <label className="form-label">
                Phone Number for Payment <span className="required">*</span>
              </label>
              <input
                type="tel"
                placeholder="Enter phone number (e.g., 255712345678)"
                value={phoneNumber}
                onChange={handlePhoneChange}
                disabled={loading}
                className="form-input"
                autoFocus
              />
              <div className="form-hints">
                {isDefaultPhone && (
                  <span className="form-hint success">
                    <CheckCircle size={14} />
                    This is the phone number you registered with
                  </span>
                )}
                <span className="form-hint">
                  <Smartphone size={14} />
                  You'll receive an STK Push to complete payment
                </span>
                <span className="form-hint">
                  <Shield size={14} />
                  Format: 255712345678 or 0712345678
                </span>
              </div>
            </div>

            {error && (
              <div className="message-box error animate-fade-in">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            {message && (
              <div className="message-box success animate-fade-in">
                <CheckCircle size={18} />
                <span>{message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !phoneNumber || feeLoading}
              className="payment-btn"
            >
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner-white"></span>
                  Processing...
                </span>
              ) : (
                <>
                  <Smartphone className="btn-icon" size={18} />
                  Pay TZS {formattedFee}
                </>
              )}
            </button>
          </form>

          <div className="payment-features">
            <div className="feature-item">
              <CheckCircle size={16} />
              <span>Secure payment</span>
            </div>
            <div className="feature-item">
              <CheckCircle size={16} />
              <span>Instant activation</span>
            </div>
          </div>

          <div className="payment-footer">
            <button
              onClick={handleBackToLogin}
              className="back-link"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default RegistrationPaymentPage;